<template>
	<div class="reservation-data">
		<div class="section" :class="{highlight: nameHighlight}">
			<div class="section-title">{{ getString("name") }}</div>
			<div class="section-content-input">
				<MenuTextInput ref="name-input" :placeholder="getString('name')" @pressed-enter="focusEmail" :value="menusStore.savedData.name ? menusStore.savedData.name : ''" />
			</div>
		</div>
		<div class="section" :class="{highlight: emailHighlight}">
			<div class="section-title">{{ getString("email") }}</div>
			<div class="section-content-input">
				<MenuTextInput ref="email-input" :placeholder="getString('email')" type="email" @pressed-enter="focusPhone" :value="menusStore.savedData.email ? menusStore.savedData.email : ''" />
			</div>
		</div>
		<div class="section" :class="{highlight: phoneHighlight}">
			<div class="section-title">{{ getString("phone") }}</div>
			<div class="section-content-input section-phone-input">
				<PhonePrefixInput />
				<MenuTextInput ref="phone-input" :placeholder="getString('phone_number')" type="phone" :value="menusStore.savedData.phone ? menusStore.savedData.phone : ''" />
			</div>
		</div>
		<div class="section" v-if="reservationsStore.selectedExperience.info_children">
			<div class="section-title">{{ getString("how_many_children") }}</div>
			<div class="section-content-input">
				<MenuQuantityButtons ref="children-input" />
			</div>
		</div>
		<div class="section" v-if="reservationsStore.selectedExperience.info_dogs">
			<div class="section-title">{{ getString("how_many_dogs") }}</div>
			<div class="section-content-input">
				<MenuQuantityButtons ref="dogs-input" />
			</div>
		</div>
		<div class="section" v-if="reservationsStore.selectedExperience.info_special">
			<div class="section-title">{{ getString("special_occasion") }}</div>
			<div class="section-content-input">
				<SpecialOccation ref="special-input" />
			</div>
		</div>
		<div class="section" v-if="reservationsStore.selectedExperience.info_allergens">
			<div class="section-title">{{ getString("allergies") }}</div>
			<div class="section-content-input">
				<MenuButton :title="allergiesButtonTitle" @click="menusStore.allergensOpen = true" outline />
			</div>
		</div>
		<div class="section" :class="{highlight: customHighlight}" v-if="menusStore.business.options.reservations_question">
			<div class="section-title">{{ menusStore.business.options.reservations_question }}</div>
			<div class="section-content-input">
				<MenuTextInput ref="custom-input" :placeholder="getString('answer')" />
			</div>
		</div>
		<div class="section">
			<div class="section-title">{{ getString('notes') }}</div>
			<div class="section-content-input">
				<MenuTextInput ref="notes-input" :placeholder="getString('notes')" />
			</div>
		</div>
		<div class="section privacy-container" v-if="menusStore.business.options.force_privacy && menusStore.acceptPrivacy === null">
			<div class="section-title">{{ getString("data_processing") }}</div>
			<div class="privacy-accept" @click="privacyAcceptClick">
				<div class="privacy-text"><p><a @click.stop :href="privacyUrl" target="_blank">{{ getString("privacy_read_link") }}</a>{{ " "+getString('allow_marketing') }}</p></div>
				<div class="privacy-refuse" v-html="getIcon('iconX')" @click.stop="privacyRefuseClick"></div>
			</div>
		</div>
		<div class="section-bottom" v-else-if="paymentRequired && paymentRequired > 0">
			<p v-if="onlyPreAuth" class="preauth-explain">{{ getString('authorize_explain', [currencyStore.readableCurrencyValue(paymentRequired)]) }}</p>
			<p v-if="onlyPreAuth && noShowAmount" class="preauth-explain">{{ getString(noShowAmount === paymentRequired ? 'noshow_full_explain' : 'noshow_explain', [currencyStore.readableCurrencyValue(noShowAmount)]) }}</p>
			<p v-if="!onlyPreAuth && !noShowAmount" class="preauth-explain">{{ getString('pay_explain', [currencyStore.readableCurrencyValue(paymentRequired)]) }}</p>
			<p v-if="!onlyPreAuth && noShowAmount" class="preauth-explain">{{ getString(noShowAmount === paymentRequired ? 'pay_explain_penalty_full' : 'pay_explain_penalty', [currencyStore.readableCurrencyValue(paymentRequired), currencyStore.readableCurrencyValue(noShowAmount)]) }}</p>
			<div class="cta-quick-actions">
				<MenuButton :title="onlyPreAuth ? getString('authorize') : getString('pay_now')" :logos-carousel="actionsStore.primaryLogos" @click="confirmReservation" />
			</div>
			<div class="ctas-loader" :class="{hidden: !loading}">
				<AppLoader color="var(--theme-txt2-color)" v-if="loading" :size="20" />
			</div>
		</div>
		<div class="section-bottom" v-else>
			<AppLoader color="var(--theme-txt2-color)" v-if="loading" :size="20" />
			<MenuButton :title="getString('confirm')" @click="confirmReservation" v-else />
		</div>
		<AppBottomSheet id="stripe-bottom-sheet" :forceWidth="bottomSheetWidth" :active="stripeActive" @toggling-popup="togglingStripe">
			<StripeCheckout v-if="menusStore.paymentMethodsAvailable.includes('stripe')" :cta="onlyPreAuth ? 'authorize_x' : 'pay_x'" :client-secret="clientSecret" :order-key="orderKey" :redirect-url="redirectUrl" :payment-methods="paymentMethods" :amount="paymentRequired" @remove-pm="removePm" :auth-only="onlyPreAuth" />
		</AppBottomSheet>
	</div>
</template>

<script>
import MenuTextInput from "@/new-menu/components/generic/MenuTextInput.vue";
import {useCurrencyStore, useLanguageStore, useNotificationStore, useScriptStore, useSvgStore} from "@/shared/stores";
import PhonePrefixInput from "@/new-menu/components/order/PhonePrefixInput.vue";
import MenuButton from "@/new-menu/components/generic/MenuButton.vue";
import {useReservationsStore} from "@/book/stores";
import MenuQuantityButtons from "@/new-menu/components/generic/MenuQuantityButtons.vue";
import SpecialOccation from "@/book/components/SpecialOccation.vue";
import AllergensFilter from "@/new-menu/components/modals/AllergensFilter.vue";
import {useActionsStore, useCartStore, useMenusStore} from "@/new-menu/stores";
import {doubleFigure, formatDateOnly, localToServerTime} from "@/shared/helpers/dates";
import {validateEmail, validatePhone} from "@/shared/helpers/strings";
import API from "@/shared/helpers/API";
import StripeCheckout from "@/new-menu/components/order/StripeCheckout.vue";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import OrderOtherCtas from "@/new-menu/components/order/OrderOtherCtas.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import AppLoader from "@/shared/components/AppLoader.vue";

export default {
	setup() {
		const languageStore = useLanguageStore();
		const menusStore = useMenusStore();
		const reservationsStore = useReservationsStore();
		const notificationStore = useNotificationStore();
		const actionsStore = useActionsStore();
		const scriptStore = useScriptStore();
		const currencyStore = useCurrencyStore();
		const svgStore = useSvgStore();
		return {getString: languageStore.getString, getIcon: svgStore.get, reservationsStore, menusStore, notificationStore, actionsStore, scriptStore, currencyStore};
	},
	name: "ReservationData",
	components: {
		AppLoader,
		OrderOtherCtas,
		AppBottomSheet,
		StripeCheckout,
		SpecialOccation, MenuQuantityButtons, MenuButton, PhonePrefixInput, MenuTextInput, AllergensFilter},
	mixins: [WindowWidth],
	data() {
		return {
			nameHighlight: false,
			emailHighlight: false,
			phoneHighlight: false,
			customHighlight: false,
			otherPaymentsActive: false,
			stripeActive: false,
			clientSecret: null,
			orderKey: null,
			redirectUrl: null,
			paymentMethods: [],
			loading: false,
		}
	},
	computed: {
		privacyUrl() {
			return this.menusStore.business.options.custom_privacy ? this.menusStore.business.options.custom_privacy : "/privacy/";
		},
		allergiesButtonTitle() {
			if(this.menusStore.allergensFilterNum > 0) {
				if(this.menusStore.allergensFilterNum === 1) {
					return this.getString("allergy_selected");
				} else {
					return this.getString("allergies_selected", [this.menusStore.allergensFilterNum]);
				}
			} else {
				return this.getString("allergens");
			}
		},
        paymentRequired() {
			if(this.menusStore.business.options.reservations_request_card && this.reservationsStore.selectedExperience.price) {

				// Check if number of people is below the card_min_people threshold
				if(this.reservationsStore.selectedExperience.card_min_people &&
				   this.reservationsStore.chosenPeople < this.reservationsStore.selectedExperience.card_min_people) {
					return 0;
				}

				let slot = this.reservationsStore.getSlotFromId(this.reservationsStore.chosenTime.slotId);
				if(slot && slot.dontRequestCard) {
					return 0;
				} else if(slot && slot.price) {
					return slot.price;
				} else {
					return this.reservationsStore.selectedExperience.price;
				}
			} else {
				return 0;
			}
        },
		onlyPreAuth() {
			return this.menusStore.business.options.reservations_request_card && this.reservationsStore.selectedExperience.price ? this.menusStore.business.options.reservations_request_card === "auth" : false;
		},
		noShowAmount() {
			return this.paymentRequired && this.menusStore.business.options.reservations_penalty ? Math.round((this.menusStore.business.options.reservations_penalty/100)*this.paymentRequired) : 0;
		},
		bottomSheetWidth() {
			if(this.windowWidth < 550) {
				return "100%";
			} else {
				return "550px";
			}
		},
	},
	methods: {
		privacyAcceptClick() {
			this.menusStore.acceptPrivacy = true;
			this.menusStore.saveData();
		},
		privacyRefuseClick() {
			this.menusStore.acceptPrivacy = false;
			this.menusStore.saveData();
		},
		removePm(v) {
			this.paymentMethods = this.paymentMethods.filter(pm => pm.id !== v);
		},
		togglingStripe(active) {
			if(!active) {
				this.stripeActive = active;
			}
		},
		focusEmail() {
			this.$refs['email-input'].focus();
		},
		focusPhone() {
			this.$refs['phone-input'].focus();
		},
		confirmReservation() {

			let name = this.$refs['name-input'].currentValue;
			if(!name || name.length < 2) {
				this.notificationStore.showNotification("missing_customer_name");
				this.$refs['name-input'].focus();
				this.nameHighlight = true;
				setTimeout(() => {
					this.nameHighlight = false;
				}, 1200);
				return;
			}

			let email = this.$refs['email-input'].currentValue;
			if(!email || !validateEmail(email)) {
				this.notificationStore.showNotification("missing_customer_email");
				this.focusEmail();
				this.emailHighlight = true;
				setTimeout(() => {
					this.emailHighlight = false;
				}, 1200);
				return;
			}

			let phone = this.$refs['phone-input'].currentValue;
			if(!phone || !validatePhone(phone)) {
				this.notificationStore.showNotification("missing_customer_phone");
				this.focusPhone();
				this.phoneHighlight = true;
				setTimeout(() => {
					this.phoneHighlight = false;
				}, 1200);
				return;
			}
            let phoneFull = this.menusStore.currentCountry ? this.menusStore.currentCountry.prefix_number + phone : phone;

			let custom = null;
			if(this.$refs['custom-input']) {
				custom = this.$refs['custom-input'].currentValue;
			}
			if((!custom || custom.length < 2) && this.menusStore.business.options.reservations_question) {
				this.notificationStore.showNotification("missing_custom_answer");
				this.$refs['custom-input'].focus();
				this.customHighlight = true;
				setTimeout(() => {
					this.customHighlight = false;
				}, 1200);
				return;
			}

			let notes = this.$refs['notes-input'].currentValue;
			let children = null;
			if(this.$refs['children-input']) {
				children = this.$refs['children-input'].currentValue;
			}
			let dogs = null;
			if(this.$refs['dogs-input']) {
				dogs = this.$refs['dogs-input'].currentValue;
			}
			let special = null;
			if(this.$refs['special-input']) {
				special = this.$refs['special-input'].currentOccasion;
			}

			let fullDate = new Date(formatDateOnly(this.reservationsStore.chosenDate)+" " + this.reservationsStore.chosenTime.time);
			let serverDate = localToServerTime(fullDate);
			let time = doubleFigure(serverDate.getHours()) + ":" + doubleFigure(serverDate.getMinutes());

			let data = {
				experience_id: this.reservationsStore.selectedExperience.experience_id,
				date: formatDateOnly(serverDate),
				slot_id: this.reservationsStore.chosenTime.slotId,
				time: time,
				people: this.reservationsStore.chosenPeople,
				name: name,
				email: email,
				phone: phoneFull,
				notes: notes,
				children: children,
				dogs: dogs,
				special: special,
				custom: custom,
				allergens: JSON.stringify(this.menusStore.allergens.filter(a => !a.active).map(a => a.allergen_id)),
			}

			this.loading = true;
			API.init().newReservation(this.menusStore.business.business_id, data, (r) => {

				this.loading = false;
				if(r && r.result) {
					if(r.result === "OK") {
						//this.reservationsStore.reservationSuccess(r.reservation_id);

						if(this.paymentRequired) {
							this.clientSecret = r.payment_intent.client_secret;
							this.redirectUrl = r.order_url;
							this.orderKey = r.order_key;
							this.orderAmount = r.payment_intent.amount;
							this.paymentMethods = r.payment_methods ? r.payment_methods : [];
							this.payPrimary();
						} else {
							/*if(r.reservation_id) {
								this.reservationsStore.confirmedReservationId = r.reservation_id;
							}*/
							window.location.href = r.order_url;
						}

					} else if(r.result === "KO" && r.details) {
						this.handleError(r.details);
					} else {
						this.handleError(false);
					}
				} else {
					this.handleError(false);
				}
			});

			let cartStore = useCartStore();
			cartStore.customerEmail = data.email;
			cartStore.customerPhone = phone;
			cartStore.customerName = data.name;
			this.menusStore.saveData();

			this.reservationsStore.customerData = {
				name: data.name,
				email: data.email,
				phone: data.phone,
				children: data.children,
				dogs: data.dogs,
				special: data.special,
				custom: data.custom,
				notes: data.notes,
				allergens: data.allergens,
			}

		},
		payPrimary() {

			if(!this.scriptStore.loadedScripts.includes("stripe")) {
				this.notificationStore.showNotification("loading_stripe");
				this.scriptStore.loadScript("stripe", () => {});
				return;
			}

			this.stripeActive = true;

		},
		handleError(error) {
			if(["availability", "tables"].includes(error)) {
                this.notificationStore.showNotification("availability_error");
			} else {
				this.notificationStore.showNotification("reservation_error");
			}
		}
	},
	mounted() {

		if(!this.scriptStore.loadedScripts.includes("stripe")) {
			this.scriptStore.loadScript("stripe", () => {});
		}

	}
}
</script>

<style lang="scss">
#stripe-bottom-sheet {
	background-color: var(--theme-txt1-alpha60-color);
	backdrop-filter: blur(6px);
	-webkit-backdrop-filter: blur(6px);
	transition: visibility 0.5s ease-in-out, opacity 0.5s ease-in-out, backdrop-filter 0.5s ease 0.3s, -webkit-backdrop-filter 0.5s ease 0.3s;

	&.hidden {
		opacity: 0;
		backdrop-filter: blur(0px);
		-webkit-backdrop-filter: blur(0px);
		background-color: var(--theme-txt1-alpha60-color);
	}

	.bottom-sheet {
		padding: 0;
		background: var(--theme-bg2-color);
		border-radius: var(--rounded-corner-l) var(--rounded-corner-l) 0 0;

		.bottom-sheet-measurer {
			border-radius: var(--rounded-corner-l) var(--rounded-corner-l) 0 0;
		}
	}
}
</style>
<style scoped lang="scss">
.reservation-data {
	display: flex;
	flex-direction: column;
	background-color: var(--theme-bg2-color);
	width: 100%;
	border-radius: var(--rounded-corner);
	padding: var(--padding-vertical) var(--padding-horizontal);

	.section {
		position: relative;
		margin-bottom: var(--padding-vertical);

		&:after {
			content: "";
			display: flex;
			position: absolute;
			top: calc(var(--padding-vertical) * -1);
			left: calc(var(--padding-horizontal) * -1);
			width: calc(100% + (var(--padding-horizontal) * 2));
			height: calc(100% + (var(--padding-vertical) * 2));
			border-radius: var(--rounded-corner);
			background: var(--business-color);
			opacity: 0;
			transition: opacity 0.5s ease-in-out;
			pointer-events: none;
		}

		&.highlight {

			&:after {
				opacity: 0.25;
			}

		}
	}

	.section-bottom {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		margin-top: var(--padding-vertical);
		gap: var(--padding-vertical);
		position: relative;

		.menu-button {
			width: 100%;
		}

		.preauth-explain {
			width: 100%;
			color: var(--theme-txt2-color);
			font-size: var(--font-size-s);
			line-height: var(--line-height-s);
			font-weight: 400;
		}

		.cta-quick-actions {
			display: flex;
			width: 100%;
			gap: 8px;

			.menu-button {
				flex-grow: 1;
				padding: 8px var(--padding-horizontal);
				min-height: 44px;

			}

		}

		.cta-other-actions {
			display: flex;
			width: 100%;
			gap: 8px;

			.menu-button {
				flex-grow: 1;
				padding: 6px var(--padding-horizontal);
				min-height: 44px;

				&.button-cash {
					width: 70px;
				}
			}
		}

		.ctas-loader {
			position: absolute;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
			display: flex;
			justify-content: center;
			align-items: center;
			background-color: var(--theme-txt1-alpha60-color);
			backdrop-filter: blur(6px);
			-webkit-backdrop-filter: blur(6px);
			transition: visibility 0.5s ease-in-out, opacity 0.5s ease-in-out, backdrop-filter 0.5s ease 0.3s, -webkit-backdrop-filter 0.5s ease 0.3s;
			visibility: visible;
			opacity: 1;
			border-radius: calc(var(--rounded-corner) - 3px);

			&.hidden {
				opacity: 0;
				backdrop-filter: blur(0px);
				-webkit-backdrop-filter: blur(0px);
				visibility: hidden;
			}
		}
	}

	.section-title {
		margin-bottom: 4px;
		font-size: var(--font-size);
		line-height: var(--line-height);
		color: var(--theme-txt1-color);
		font-weight: 500;

		&.with-subtitle {
			margin-bottom: calc(var(--padding-vertical) / 2);
		}
	}

	.section-subtitle {
		margin-bottom: var(--padding-vertical);
		font-size: var(--font-size-s);
		line-height: var(--line-height-s);
		color: var(--theme-txt2-color);
		font-weight: 400;
	}

	.section-content-input {

		&:deep(input), &:deep(textarea) {
			padding: var(--padding-vertical) var(--padding-horizontal-s);
			background: var(--theme-bg1-color);
			border-radius: var(--rounded-corner);
			width: 100%;
			min-height: 40px;
		}

		&.section-phone-input {
			gap: 4px;
			display: flex;
			justify-content: flex-start;
			align-items: stretch;

			.menu-text-input {
				flex-grow: 1;
			}
		}
	}

	.privacy-container {
		display: flex;
		flex-direction: column;

		.privacy-title {
			font-size: var(--font-size);
			line-height: var(--line-height);
			color: var(--theme-txt1-color);
			font-weight: 700;
			margin-bottom: 8px;
		}

		.privacy-accept {
			display: flex;
			flex-direction: column;
			background-color: var(--theme-bg1-color);
			border-radius: var(--rounded-corner);
			border: 1px solid var(--theme-txt1-color);
			padding: 8px var(--padding-horizontal-s);
			cursor: pointer;
			transition: background-color 0.3s ease-in-out;
			position: relative;

			.privacy-refuse {
				position: absolute;
				right: 12px;
				top: 12px;
				cursor: pointer;
				display: flex;

				&:deep(svg) {
					width: 10px;
					height: 10px;

					.fill {
						fill: var(--theme-txt1-color);
					}

					.stroke {
						stroke: var(--theme-txt1-color);
					}
				}
			}

			&:hover {
				background-color: var(--theme-bg2-color);
			}

			.privacy-text {
				display: flex;
				margin-left: 20px;
				position: relative;
				padding-right: 18px;

				&:before {
					content: '';
					height: 15px;
					width: 15px;
					background-color: var(--theme-bg2-color);
					position: absolute;
					left: -24px;
					top: calc(50% - 8px);
					border: 1px solid var(--theme-txt1-color);
					border-radius: var(--rounded-corner-s);
				}

				a {
					text-decoration: none;
					font-weight: 500;
					font-size: var(--font-size-s);
					line-height: var(--line-height-s);
					color: var(--business-color) !important;

					&:after {
						display: none;
					}
				}

				p {
					font-size: var(--font-size-s);
					line-height: var(--line-height-s);
					color: var(--theme-txt2-color);
					margin: 0;
				}

			}
		}
	}

}
</style>